if (!customElements.get('phc-bonnet-pair')) {
  customElements.define('phc-bonnet-pair', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = document.getElementById(this.dataset.formId);
      this.productInfo = this.closest('product-info');
      if (!this.form || !this.productInfo) return;
      const config = JSON.parse(this.querySelector('[data-pair-config]').textContent);
      this.labels = config.labels;
      this.primaryVariants = config.primary;
      this.variants = config.variants;
      this.active = false;
      this.busy = false;
      this.pending = false;
      this.size = '';
      this.color = '';
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.submitLabel = this.submitButton?.querySelector('span');
      if (!this.submitButton || !this.submitLabel) return;
      this.quantity = this.productInfo.querySelector('[name="quantity"]');
      this.toggle = this.querySelector('[data-pair-toggle]');
      this.panel = this.querySelector('[data-pair-panel]');
      this.totals = this.querySelector('[data-pair-totals]');
      this.error = this.querySelector('[data-pair-error]');
      this.colors = Array.from(this.querySelectorAll('[data-pair-color]'));
      this.sizes = Array.from(this.querySelectorAll('[data-pair-size]'));
      this.submitButton.before(this.totals);
      this.toggle.hidden = false;
      this.querySelector('[data-pair-gift]').hidden = false;
      this.money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency',currency:this.dataset.currency}).format(cents / 100);
      this.toggle.addEventListener('click', () => {
        this.active = true;
        this.savedQuantity = this.quantity?.value || '1';
        if (this.quantity) this.quantity.value = '1';
        this.error.hidden = true;
        this.render();
        this.sizes.find(input => !input.disabled)?.focus({preventScroll:true});
      }, options);
      this.querySelector('[data-pair-remove]').addEventListener('click', () => {
        this.active = false;
        if (this.quantity) this.quantity.value = this.savedQuantity;
        this.error.hidden = true;
        this.render();
        this.toggle.focus();
      }, options);
      this.sizes.forEach(input => input.addEventListener('change', () => {
        this.size = input.value;
        if (!this.selectedSecond()) this.color = '';
        this.querySelector('[data-pair-out-of-stock]').open = false;
        this.error.hidden = true;
        this.render();
      }, options));
      this.colors.forEach(button => button.addEventListener('click', () => {
        this.color = button.dataset.pairColor;
        this.error.hidden = true;
        this.render();
      }, options));
      this.productInfo.addEventListener('change', event => {
        if (event.target.matches('input[name="id"], [name="quantity"]')) this.render();
      }, options);
      this.quantity?.addEventListener('input', () => this.render(), options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeOption = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({data}) => {
          if (!this.productInfo.contains(data.event.target)) return;
          this.pending = true;
          this.render();
        });
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({data}) => {
          if (data.sectionId !== this.productInfo.dataset.section) return;
          this.pending = false;
          this.render();
        });
      }
      this.form.addEventListener('submit', event => this.addPair(event), {...options,capture:true});
      window.addEventListener('pageshow', () => { this.busy = false; this.pending = false; this.render(); }, options);
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribeOption?.();
      this.unsubscribeVariant?.();
      this.totals?.remove();
      this.controller = null;
    }

    selectedFirst() {
      const id = Number(this.form.querySelector('[name="id"]')?.value);
      return this.primaryVariants.find(variant => variant.id === id && variant.available);
    }

    selectedSecond() {
      return this.variants.find(variant => variant.available && variant.size === this.size && variant.color === this.color);
    }

    render() {
      const first = this.selectedFirst();
      const second = this.active ? this.selectedSecond() : null;
      const quantity = this.active ? 1 : Number(this.quantity?.value || 1);
      const validQuantity = Number.isInteger(quantity) && quantity > 0 && quantity <= 99;
      const ready = Boolean(first && validQuantity && (!this.active || second));
      const availableGrid = this.querySelector('[data-pair-available]');
      const unavailableGrid = this.querySelector('[data-pair-unavailable]');
      let unavailableCount = 0;
      this.dataset.pairActive = String(this.active);
      this.toggle.hidden = this.active;
      this.toggle.disabled = this.busy;
      this.toggle.setAttribute('aria-expanded', String(this.active));
      this.panel.hidden = !this.active;
      this.querySelector('[data-pair-remove]').disabled = this.busy;
      this.sizes.forEach(input => {
        input.disabled = this.busy || !this.variants.some(variant => variant.available && variant.size === input.value);
        input.checked = input.value === this.size;
      });
      this.colors.forEach(button => {
        const available = this.variants.some(variant => variant.available && variant.color === button.dataset.pairColor && (!this.size || variant.size === this.size));
        button.disabled = this.busy || !this.size || !available;
        button.setAttribute('aria-pressed', String(button.dataset.pairColor === this.color));
        button.classList.toggle('is-unavailable', !available);
        button.title = button.dataset.pairColor + (available ? '' : ' · ' + this.labels.soldOut);
        (available ? availableGrid : unavailableGrid).append(button);
        if (!available) unavailableCount++;
      });
      this.querySelector('[data-pair-out-of-stock]').hidden = !this.size || !unavailableCount;
      this.querySelector('[data-pair-out-size]').textContent = this.size ? ' · ' + this.size : '';
      this.querySelector('[data-pair-color-name]').textContent = this.color || (this.size ? this.labels.chooseColor : this.labels.chooseSize);
      this.querySelector('[data-pair-description]').textContent = second ? second.color + ' · ' + second.size : (this.size ? this.labels.chooseColor : this.labels.chooseSize);
      this.querySelector('[data-pair-price]').textContent = second ? this.money(second.price) : '';
      const image = this.querySelector('[data-pair-image]');
      image.hidden = !second;
      if (second) {
        image.src = second.image;
        image.alt = second.color + ' · ' + second.size;
      }
      const lines = new Map();
      if (first) lines.set(first.id, {price:first.price,quantity});
      if (second) {
        const line = lines.get(second.id);
        lines.set(second.id, {price:second.price,quantity:(line?.quantity || 0) + 1});
      }
      const count = quantity + (this.active ? 1 : 0) + Number(this.dataset.existingCount || 0);
      const rate = count >= 3 ? 25 : count >= 2 ? 20 : 0;
      let regular = 0;
      let total = 0;
      lines.forEach(line => {
        const amount = line.price * line.quantity;
        regular += amount;
        total += line.quantity * (line.price - Math.floor(line.price * rate / 100));
      });
      this.totals.hidden = !ready || !rate;
      this.totals.querySelector('[data-pair-regular]').textContent = this.money(regular);
      this.totals.querySelector('[data-pair-total]').textContent = this.money(total);
      this.totals.querySelector('[data-pair-saving]').textContent = this.labels.save + ' ' + this.money(regular - total);
      if (this.busy || this.pending || this.active) {
        this.submitButton.disabled = this.busy || this.pending || !ready;
        this.submitButton.setAttribute('aria-disabled', String(this.submitButton.disabled));
      } else if (first && !this.form.closest('product-form')?.classList.contains('loading')) {
        this.submitButton.disabled = !validQuantity;
        this.submitButton.setAttribute('aria-disabled', String(!validQuantity));
      }
      if (this.busy) this.submitLabel.textContent = this.labels.adding;
      else if (this.active && !second) this.submitLabel.textContent = this.labels.chooseSecond;
      else if (ready) this.submitLabel.textContent = (this.active ? this.labels.addTwo : this.labels.add) + ' — ' + this.money(total);
    }

    async addPair(event) {
      if (!this.active) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy || this.pending || !this.selectedFirst() || !this.selectedSecond()) return;
      if (!this.form.checkValidity()) { this.form.reportValidity(); return; }
      const first = this.selectedFirst();
      const second = this.selectedSecond();
      const properties = {};
      const formData = new FormData(this.form);
      for (const [key,value] of formData) {
        if (key.startsWith('properties[') && key.endsWith(']')) properties[key.slice(11,-1)] = value;
      }
      const primary = {id:first.id,quantity:1};
      if (Object.keys(properties).length) primary.properties = properties;
      if (formData.get('selling_plan')) primary.selling_plan = formData.get('selling_plan');
      const sameLine = first.id === second.id && !primary.properties && !primary.selling_plan;
      const items = sameLine ? [{id:first.id,quantity:2}] : [primary,{id:second.id,quantity:1}];
      this.busy = true;
      this.error.hidden = true;
      this.render();
      const prefix = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(prefix + 'cart/add.js', {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items})});
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.labels.error);
        window.location.assign(prefix + 'cart');
      } catch (failure) {
        this.busy = false;
        this.error.textContent = failure.message || this.labels.error;
        this.error.hidden = false;
        this.render();
      }
    }
  });
}
